import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";

import { i18n, loadLanguageAsync } from "@/i18n";

import { DataClient } from "@/services/DataClient";
import { Component } from "vue";
import { Utilities } from "@/services/Utilities";

Vue.use(VueRouter);

// const supportedLocales = ["es", "en", "it", "fr", "de"];
// const defaultLocale = "en";

// function getFilteredUserLocale() {
//     const userLocale = navigator.language || (navigator as any).userLanguage || defaultLocale;
//     const userLocaleShort = userLocale.split("-")[0];

//     return supportedLocales.includes(userLocaleShort) ? userLocaleShort : defaultLocale;
// }

interface TranslatableRoute {
    name: string;
    paths: {
        [key: string]: string;
    };
    component: Component | (() => Promise<any>);
    meta?: any;
}

const translatableRoutes: Array<TranslatableRoute> = [
    {
        name: "home",
        paths: {
            es: "/inicio",
            en: "/home",
            fr: "/accueil",
            de: "/startseite",
            it: "/inizio",
            ro: "/acasa"
        },
        component: () => import(/* webpackChunkName: "home" */ "../views/HomePageView.vue")
    },
    {
        name: "transfer-booking",
        paths: {
            es: "/transfer-booking-process/:id",
            en: "/transfer-booking-process/:id",
            fr: "/transfer-booking-process/:id",
            de: "/transfer-booking-process/:id",
            it: "/transfer-booking-process/:id",
            ro: "/transfer-booking-process/:id",
        },
        component: () => import(/* webpackChunkName: "home" */ "../views/TransferBookingPageView.vue")
    },
    {
        name: "cart",
        paths: {
            es: "/comprar/:token",
            en: "/purchase/:token",
            fr: "/acheter/:token",
            de: "/kaufen/:token",
            it: "/comprare/:token",
            pt: "/comprar/:token",
            pl: "/kupic/:token",
            cs: "/nakup/:token",
            ru: "/kupit/:token",
            ro: "/cumpara/:token",
        },
        meta: {
            divider: true,
        },
        component: () => import(/* webpackChunkName: "cart" */ "../views/CartPageView.vue")
    },
    {
        name: "activities",
        paths: {
            es: "/actividades",
            en: "/activities",
            fr: "/activites",
            de: "/aktivitaten",
            it: "/attivitas",
            pt: "/atividades",
            pl: "/aktywnosci",
            cs: "/aktivity",
            ru: "/aktivity",
            ro: "/activitati"
        },
        component: () => import(/* webpackChunkName: "activities" */ "../views/ActivitiesPageView.vue")
    },
    {
        name: "activity",
        paths: {
            es: "/actividad/:id",
            en: "/activity/:id",
            fr: "/activite/:id",
            de: "/aktivitat/:id",
            it: "/attivita/:id",
            ro: "/activitate/:id"
        },
        meta: {
            divider: false,
        },
        component: () => import(/* webpackChunkName: "activities" */ "../views/ActivityPageView.vue"),
    },
    {
        name: "privacy-policy",
        paths: {
            es: "/politica-de-privacidad",
            en: "/privacy-policy",
            fr: "/politique-de-confidentialite",
            de: "/datenschutzrichtlinie",
            it: "/politica-di-privacy",
            pt: "/politica-de-privacidade",
            pl: "/polityka-prywatnosci",
            cs: "/ochrana-osobnich-udaju",
            ru: "/politika-konfidencialnosti",
            ro: "/politica-de-confidentialitate"
        },
        component: () => import(/* webpackChunkName: "extra-pages" */ "../views/PrivacyPolicyPageView.vue")
    },

];

export function initializeRouter(): { router: VueRouter, initialLocale: string } {
    const supportedLocales = DataClient.instance.supportedLocales;
    const defaultLocale = DataClient.instance.defaultLocale;

    const routes: Array<RouteConfig> = [
        {
            path: "/",
            redirect: () => {
                const supportedLocales = DataClient.instance.supportedLocales;
                const defaultLocale = DataClient.instance.defaultLocale;

                const targetLocale = Utilities.getBrowserLanguage();
                return supportedLocales.includes(targetLocale) ? targetLocale : defaultLocale;
            }
        }, {
            name: "redirector",
            path: "/r",
            component: () => import(/* webpackChunkName: "home" */ "../components/Redirector.vue"),
        }
    ];

    supportedLocales.forEach((locale) => {
        routes.push({
            path: `/${locale}`,
            redirect: { name: `home.${locale}` }
        });
    });

    translatableRoutes.forEach((route) => {
        supportedLocales.forEach((locale) => {
            const path = route.paths[locale];

            if (path) {
                routes.push({
                    name: `${route.name}.${locale}`,
                    path: `/${locale}${path}`,
                    component: route.component,
                    meta: route.meta
                });
            }
        });
    });

    const router = new VueRouter({
        mode: "history",
        base: process.env.BASE_URL,
        routes,
        scrollBehavior: (to, from, savedPosition) => {
            if (savedPosition) {
                return savedPosition;
            } else if (to.hash) {
                setTimeout(() => {
                    const element = document.getElementById(to.hash.replace(/#/, ''))

                    // scroll to the element y
                    if (element) {
                        window.scrollTo({ top: element.offsetTop, behavior: 'smooth' })
                    }

                }, 500)

                //NOTE: This doesn't work for Vue 3
                //return { selector: to.hash }

            } else {
                return { x: 0, y: 0 };
            }
        }
    });

    router.beforeEach(async (to, from, next) => {
        if (to.name === "redirector") {
            next();
            return;
        }

        const targetLocale = ((to.path || "").split("/")[1] || "").toLowerCase();
        const locale = supportedLocales.includes(targetLocale) ? targetLocale : defaultLocale;

        if (locale !== i18n.locale) {
            await loadLanguageAsync(locale);
        }

        await DataClient.instance.loadConfiguration(locale);

        next();
    });

    const targetLocale = window.location.pathname.split("/")[1] || Utilities.getBrowserLanguage();
    const initialLocale = supportedLocales.includes(targetLocale) ? targetLocale : defaultLocale;

    return { router, initialLocale };
}
